import {
  useWSModalCustom,
  WSAlert,
  WSInfoBox,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSTable,
  WSTableCell,
  WSTableColumn
} from "@wingspanhq/fe-component-library";
import { IRequirementDefinitionResponse } from "@wingspanhq/payments/dist/interfaces";
import { IRequirementDefinitionUpdate } from "@wingspanhq/payments/dist/interfaces/requirement";
import {
  RequirementDefinitionChangePreview,
  RequirementDefinitionUpdateHelpers
} from "@wingspanhq/payments/dist/services/requirements/requirementDefinitionUpdateHelpers";
import React, { useMemo } from "react";

type ComparisonRow = {
  newer: string;
  older: string;
};

type ModalLabels = RequirementDefinitionChangePreview["modalLabels"];

const buildComparisonRows = (labels: ModalLabels): ComparisonRow[] => {
  const rows: ComparisonRow[] = [];

  if (labels.renewals) {
    rows.push({
      newer: labels.renewals.after,
      older: labels.renewals.before
    });
  }

  if (labels.expiration) {
    rows.push({
      newer: labels.expiration.after,
      older: labels.expiration.before
    });
  }

  if (labels.approval) {
    rows.push({
      newer: labels.approval.after,
      older: labels.approval.before
    });
  }

  return rows;
};

const buildTableData = (
  rows: ComparisonRow[]
): Array<{ data: ComparisonRow; id: string }> =>
  rows.map((row, index) => ({
    data: row,
    id: String(index)
  }));

const TABLE_COLUMNS: WSTableColumn<ComparisonRow>[] = [
  {
    config: {
      gridTemplateSizeMax: "1fr",
      header: { text: "Older version" }
    },
    renderFunction: ({ data }) => <WSTableCell text={data.older} />
  },
  {
    config: {
      gridTemplateSizeMax: "1fr",
      header: { text: "Newer version" }
    },
    renderFunction: ({ data }) => <WSTableCell text={data.newer} />
  }
];

type Props = {
  requirementDefinition: IRequirementDefinitionResponse;
  update: IRequirementDefinitionUpdate;
} & WSModalCustomProps<void>;

const ModalAboutRequirementVersions: React.FC<Props> = ({
  requirementDefinition,
  update,
  ...modalProps
}) => {
  const comparisonRows = useMemo(() => {
    const preview =
      RequirementDefinitionUpdateHelpers.previewRequirementDefinitionUpdate(
        requirementDefinition,
        update
      );

    return buildComparisonRows(preview.modalLabels);
  }, [requirementDefinition, update]);

  const tableData = useMemo(
    () => buildTableData(comparisonRows),
    [comparisonRows]
  );

  return (
    <WSModal
      actions={{
        alignment: "fill",
        buttons: [
          {
            kind: "Secondary",
            label: "Got it",
            onClick: () => modalProps.onClose()
          }
        ]
      }}
      size="M"
      title="About versions"
      {...modalProps}
    >
      <WSList gap="XL">
        <WSAlert
          icon="info-circle"
          size="M"
          theme="info"
          title="About versions and affected contractors"
        >
          Contractors may be disadvantaged (such as facing higher compliance
          burden) when they are kept on an older version of the requirement if a
          newer version offer improvements. See examples below.
        </WSAlert>

        <WSList gap="M">
          <WSTable<ComparisonRow>
            columns={TABLE_COLUMNS}
            tableData={tableData}
          />

          <WSInfoBox title="Pro-tip">
            Review the impact comparison of your settings to confirm it is your
            intended outcome.
          </WSInfoBox>
        </WSList>
      </WSList>
    </WSModal>
  );
};

export const useAboutRequirementVersionsModal = () =>
  useWSModalCustom(ModalAboutRequirementVersions);
